import { showNotification } from '@mantine/notifications';
import { filter } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import type { IReport } from '../../api';
import {
	queryClient,
	reportsQueryKeyFactory,
	useDeleteReport as useDeleteReportMutation,
} from '../../api';
import type { ICommandListItem } from '../../components/Spotlight/components/CommandPalette/constants';
import { useDeleteManyAction } from '../../components/TableV2/BaseModel.hooks';
import { useGenericColumns } from '../../components/TableV2/SecodaEntity.hooks';
import { BoldTextRender } from '../../components/TableV2/render';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

export function useColumns<T extends IReport>(): ExtendedDataTableColumn<T>[] {
	const genericColumns = useGenericColumns({}) as ExtendedDataTableColumn<T>[];

	const columns = useMemo(
		() => [
			{
				accessor: 'name',
				titleLabel: 'Name',
				render: (record) => <BoldTextRender record={record} field={'name'} />,
			},
			...filter(genericColumns, (column) =>
				['updated_at'].includes(column.accessor)
			),
		],
		[genericColumns]
	);

	return columns;
}

export const useDeleteReport = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [reportToDelete, setReportToDelete] = useState<string | null>(null);
	const { mutateAsync: deleteReport } = useDeleteReportMutation({});

	const openModal = useCallback((id: string) => {
		setReportToDelete(id);
		setIsOpen(true);
	}, []);

	const closeModal = useCallback(() => {
		setIsOpen(false);
		setReportToDelete(null);
	}, []);

	const handleDelete = useCallback(async () => {
		if (!reportToDelete) return;

		try {
			await deleteReport({ id: reportToDelete });
			closeModal();
			queryClient.invalidateQueries({
				queryKey: reportsQueryKeyFactory.allLists(),
			});
			showNotification({
				title: 'Success',
				message: 'Report deleted successfully',
				color: 'green',
			});
		} catch (error) {
			showNotification({
				title: 'Error',
				message: `Failed to delete the report. Error: ${error}`,
				color: 'red',
			});
		}
	}, [reportToDelete, deleteReport, closeModal]);

	return {
		isOpen,
		openModal,
		closeModal,
		handleDelete,
	};
};

export const useActions = (): ICommandListItem<IReport>[] => {
	const { mutateAsync: deleteReport } = useDeleteReportMutation({});

	const onDelete = useCallback(
		(id: string) => deleteReport({ id }),
		[deleteReport]
	);

	const { action: deleteAction } = useDeleteManyAction<IReport>({
		name: 'report',
		queryKeys: reportsQueryKeyFactory.allLists(),
		onDelete,
	});

	const actions = useMemo(() => [deleteAction], [deleteAction]);

	return actions;
};
